import { Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import Iconify from 'src/components/iconify';
import ProfileMedicationsDeleteModal from 'src/sections/@dashboard/profile/components/ProfileMedicationsDeleteModal';
import MedicationFormModal, {
  MedicationsFormModalValuesProps,
} from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import ProfileMedicationsTable from 'src/sections/@dashboard/profile/components/ProfileMedicationsTable';
import ProfileMedicationsTableSkeleton from 'src/sections/@dashboard/profile/components/ProfileMedicationsTableSkeleton';
import MedicationSourceVisitModal from 'src/sections/@dashboard/profile/components/MedicationSourceVisitModal';
import ProfileMedicationsFilters, {
  DEFAULT_MEDICATION_FILTERS,
  MedicationFilters,
} from 'src/sections/@dashboard/profile/components/ProfileMedicationsFilters';
import { buildStatusChangePayload } from 'src/sections/@dashboard/profile/components/medication-form/medicationFormPayload';
import { GetProfileMedicationsDtoLinkedReminder } from 'woofwoof-api';
import { useMedicationsScope } from 'src/sections/@dashboard/visit/hooks/useMedicationsScope';
import { facilityMedicationBody } from 'src/sections/@dashboard/visit/hooks/useMedicationWriteSeries';
import { useVisitScope } from 'src/sections/@dashboard/visit/hooks/useVisitScope';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import { useOrganizationContext } from '../../../hooks/useOrganizationContext';
import { FilterList } from '@mui/icons-material';
import { translateKey } from 'src/locales/translate';
import { forbiddenMessage } from 'src/utils/forbiddenMessage';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export function OrganizationPatientMedicationsContent() {
  const theme = useTheme();
  const { isOrganizationAdminRoleSelected } = useOrganizationContext();
  const { profileId: patientId, visitId, isVisitScoped } = useVisitScope();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [medicationId, setMedicationId] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const writeError = useStockWriteError();

  const handleOnRowClick = (id: string) => {
    setMedicationId(id);
    setIsModalOpen(true);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setMedicationId('');
  };

  const handleOnCloseModal = () => {
    setIsModalOpen(false);
    setMedicationId('');
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const { page, setPage, rowsPerPage, onChangePage, onChangeRowsPerPage } =
    useTable({ defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE });

  const [filters, setFilters] = useState<MedicationFilters>(
    DEFAULT_MEDICATION_FILTERS,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sourceVisitId, setSourceVisitId] = useState('');

  const {
    items: medications,
    total,
    isLoading,
    create: addMedication,
    update: updateMedicationInScope,
    remove: removeMedication,
    changeStatus: changeMedicationStatus,
    isCreating: isAddingMedication,
    isUpdating: isUpdatingMedication,
    isDeleting: isDeletingMedication,
    createPending,
    updatePending,
    createBlocked,
    updateBlocked,
    createFrozenValues,
    updateFrozenValues,
    createError,
    updateError,
  } = useMedicationsScope({
    page,
    rowsPerPage,
    statuses: filters.statuses,
    editingId: medicationId,
    onMutationSuccess: (kind) => {
      if (kind === 'create') return handleMedicationAdded();
      if (kind === 'update') return handleMedicationUpdated();
      handleMedicationDeleted();
    },
    // F-5: a Warehouse refusal in Polish, else the backend's reason for a
    // refusal (#707: a visit past its edit window), else this screen's copy.
    onMutationError: (kind, error) =>
      writeError(
        forbiddenMessage(error) ||
          translateKey(
            kind === 'create'
              ? 'snackbar.medicationAddError'
              : kind === 'update'
              ? 'snackbar.medicationUpdateError'
              : 'snackbar.medicationDeleteError',
          ),
      )(error),
  });

  const linkedReminder: GetProfileMedicationsDtoLinkedReminder | null =
    medicationId
      ? medications.find((m) => m.id === medicationId)?.linkedReminder ?? null
      : null;

  // R4-3: the reminder travels in the medication's own request and commits
  // with it, so a success needs no second call.
  function handleMedicationAdded(): void {
    enqueueSnackbar(translateKey('snackbar.medicationAdded'), {
      variant: 'success',
    });
    setIsModalOpen(false);
  }

  function handleMedicationUpdated(): void {
    enqueueSnackbar(translateKey('snackbar.medicationUpdated'), {
      variant: 'success',
    });
    setIsModalOpen(false);
    setTimeout(() => setMedicationId(''), 500);
  }

  function handleMedicationDeleted(): void {
    enqueueSnackbar(translateKey('snackbar.medicationDeleted'), {
      variant: 'success',
    });
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setTimeout(() => setMedicationId(''), 500);
  }

  async function handleOnSubmit(
    formData: MedicationsFormModalValuesProps,
    { recordChanged }: { recordChanged: boolean },
  ): Promise<void> {
    // R4-3: one Facility payload (dosing, stock and the reminder intent),
    // frozen by the write series with the rest of the request.
    const payload = facilityMedicationBody(formData);
    if (medicationId) {
      // Stopping is not an edit: it closes the course with a date and a
      // reason, and lands in the medication's history. F-6: a resend of an
      // unconfirmed edit sends its frozen request alone.
      const current = medications.find((m) => m.id === medicationId);
      if (!updatePending && current && formData.status !== current.status) {
        await changeMedicationStatus(
          medicationId,
          buildStatusChangePayload(formData, visitId),
        );
      }
      // A save that only moved the status sends no edit. Any other change is
      // an ordinary edit, also for a course from a locked visit (#730).
      if (!updatePending && !recordChanged) {
        handleMedicationUpdated();
        return;
      }
      updateMedicationInScope(medicationId, payload, formData);
    } else {
      addMedication(payload, formData);
    }
  }

  return (
    <>
      <Stack sx={CARD_HEADER_SX}>
        {isLoading ? (
          <Skeleton variant="rounded" height="24px" width="77px" />
        ) : (
          <Typography {...CARD_HEADER_TITLE_PROPS}>
            {translateKey('visit.tabMedications')}
          </Typography>
        )}
        <Stack direction="row" gap={1.5} alignItems="center">
          {!isLoading && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterList />}
              data-testid="medication-filters-toggle"
              onClick={() => setFiltersOpen((open) => !open)}
              color={
                filtersOpen || filters.statuses.length ? 'primary' : 'inherit'
              }
            >
              {translateKey('visit.historyFiltersTooltip')}
            </Button>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" height="30px" width="77px" />
          ) : isOrganizationAdminRoleSelected ? null : (
            <Button
              size="small"
              startIcon={<Iconify icon="eva:plus-fill" width={16} />}
              variant="contained"
              data-testid="medication-add-open"
              sx={{
                background: theme.palette.common.black,
                color: theme.palette.common.white,
              }}
              onClick={handleOpenModal}
            >
              {translateKey('common.add')}
            </Button>
          )}
        </Stack>
      </Stack>

      <ProfileMedicationsFilters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(0);
        }}
        isOpen={filtersOpen}
      />

      {isLoading ? (
        <ProfileMedicationsTableSkeleton />
      ) : (
        <ProfileMedicationsTable
          data={medications}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          onRowClick={handleOnRowClick}
          onVisitClick={setSourceVisitId}
          patientId={patientId}
        />
      )}
      <MedicationSourceVisitModal
        visitId={sourceVisitId}
        profileId={patientId}
        onClose={() => setSourceVisitId('')}
      />

      <MedicationFormModal
        profileId={patientId}
        isModalOpen={isModalOpen}
        closeModal={handleOnCloseModal}
        onSubmit={handleOnSubmit}
        onDelete={handleOpenDeleteModal}
        medication={
          medicationId
            ? medications.find((medication) => medication.id === medicationId)
            : undefined
        }
        linkedReminder={linkedReminder}
        isCreating={isAddingMedication}
        isUpdating={isUpdatingMedication}
        isDeleting={isDeletingMedication}
        canEdit={!isOrganizationAdminRoleSelected}
        isViewOnly={isOrganizationAdminRoleSelected}
        singleReminderOnly={isVisitScoped}
        stockEnabled={!isOrganizationAdminRoleSelected}
        unconfirmedSave={medicationId ? updatePending : createPending}
        unconfirmedBlocked={medicationId ? updateBlocked : createBlocked}
        unconfirmedValues={
          medicationId ? updateFrozenValues : createFrozenValues
        }
        stockWriteError={medicationId ? updateError : createError}
      />
      <ProfileMedicationsDeleteModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseDeleteModal}
        onDelete={() => removeMedication(medicationId)}
        isLoading={isDeletingMedication}
      />
    </>
  );
}
